import { useState, useCallback, useEffect } from 'react';

import type { Game } from '@shared/types';

import useAxios from '@services/api';

import { FormInputs, GameBuild } from './client-miner.types';

export const useFetchGameDefaults = (name: string) => {
  const { response, error, issueRequest } = useAxios<Game>();

  useEffect(() => {
    if (name) {
      void issueRequest({ url: `/games/${name}`, method: 'get' });
    }
  }, [name, issueRequest]);

  return response;
};

// Хук для создания игры Miner
export const useCreateGameBuild = () => {
  const { response, error, issueRequest, loading } = useAxios<GameBuild>();

  const handleGameBuildSubmit = useCallback(
    async (formData: FormData) => {
      try {
        const result = await issueRequest({
          url: `/games/build/miner`,
          method: 'put',
          body: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return result;
      } catch (err: any) {
        console.error('Ошибка при отправке формы:', err);
        throw new Error(err.message || 'Ошибка при создании сборки игры');
      }
    },
    [issueRequest]
  );

  return [response, handleGameBuildSubmit, loading] as const;
};

// Хук для получения существующей игры
export const useFetchGameBuild = (gameBuildId: string) => {
  const { response, error, issueRequest } = useAxios<GameBuild>();

  const fetchGameBuild = useCallback(() => {
    if (gameBuildId) {
      void issueRequest({ url: `/games/build/${gameBuildId}`, method: 'get' });
    }
  }, [gameBuildId, issueRequest]);

  // Автоматически делаем запрос при изменении gameBuildId
  useEffect(() => {
    if (gameBuildId && !response) {
      fetchGameBuild();
    }
  }, [gameBuildId, response, fetchGameBuild]);

  return [response, fetchGameBuild, error] as const;
};

// Хук для установки даты активации игры
export const useSetActivateGameDate = () => {
  const { response, error, issueRequest } = useAxios<Record<string, never>>();

  const setActivateGameDate = useCallback(
    (id: string) => {
      void issueRequest({ url: `/games/keys/${id}`, method: 'put' });
    },
    [issueRequest]
  );

  return [response, setActivateGameDate] as const;
}; 